import { Button } from "@material-ui/core";
import AdvancedTable from "@saleor/apps/components/AdvancedTable/AdvancedTable";
import DialogConfirmSplit from "@saleor/changeOrderDomestic/components/DialogConfirmSplit";
import MyTypography from "@saleor/components/MyTypography";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import ModalAddress from "@saleor/createOrChangeNotRefContract/components/ModalAddress/ModalAddress";
import ModalOneTimeForms from "@saleor/createOrChangeNotRefContract/components/ModalAddress/ModalOneTimeForms";
import ModalSearchForProducts from "@saleor/createOrChangeNotRefContract/components/ModalSearchForProducts";
import SaleTextModal from "@saleor/createOrChangeNotRefContract/components/SaleTextModal/SaleTextModal";
import { ITEM_STATUS } from "@saleor/createOrChangeNotRefContract/constants";
import {
  getItemByNo,
  getShipToData
} from "@saleor/createOrChangeNotRefContract/helpers";
import useAddOrderLine from "@saleor/createOrChangeNotRefContract/hooks/useAddOrderLine";
import useOrderLineShipTo from "@saleor/createOrChangeNotRefContract/hooks/useOrderLineShipTo";
import usePagination from "@saleor/createOrChangeNotRefContract/hooks/usePagination";
import useSaveSplitItem from "@saleor/createOrChangeNotRefContract/hooks/useSaveSplitItem";
import useSort from "@saleor/createOrChangeNotRefContract/hooks/useSort";
import {
  NotRefOrderQueryParams,
  NotRefOrderSortField
} from "@saleor/createOrChangeNotRefContract/urls";
import {
  checkIsCashSaleOrder,
  ItemTab
} from "@saleor/createOrChangeNotRefContract/utils";
import CashSaleForm, {
  CashSaleFormProps
} from "@saleor/createOrder/components/CashSaleForm";
import { renderCollection } from "@saleor/misc";
import { getArrowDirection, getSortParams } from "@saleor/utils/sort";
import classNames from "classnames";
import { FormikProps } from "formik";
import isEqual from "lodash/isEqual";
import React, {
  Dispatch,
  forwardRef,
  ForwardRefRenderFunction,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from "react";

import useStyles from "./styles";
import useItemDetailsData from "./useItemDetailsData";

export interface AddEditItemDetailsNotRefProps {
  formik: FormikProps<any>;
  formikHeader?: FormikProps<NotRefOrderHeader>;
  params: NotRefOrderQueryParams;
  selected: string[];
  onSelect: (key: string) => void;
  onSelectAll?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  orderId?: string;
  isViewMode?: boolean;
  openChangeOrderAdd: boolean;
  setOpenChangeOrderAdd?: (e: boolean) => void;
  isChangeFlow: boolean;
  currentAction: "none" | "add" | "split" | "edit";
  changeCurrentAction?: (a: "none" | "add" | "split" | "edit") => void;
  deleteChangeOrderLines?: (e: string, setIsDeleteSuccess: any) => void;
  isNewItemAdded?: boolean;
  setSubmitState: Dispatch<SetStateAction<any>>;
  resetSubmitState: () => void;
  handleSubmitResponse: (responseData, errors) => void;
  undoCancelOrderLine?: (e: string) => void;
  setCurrentAction?: (action) => void;
  tableLoading: boolean;
  setTableLoading: (loading: boolean) => void;
  totalItemsOriginal?: number;
  disabledActionForCashSale?: boolean;
  cashSaleFormProps?: CashSaleFormProps;
}

export interface AddEditItemDetailsNotRefRef {
  forceChangeTab: (tab: ItemTab) => void;
}

const AddEditItemDetailsNotRef: ForwardRefRenderFunction<
  AddEditItemDetailsNotRefRef,
  AddEditItemDetailsNotRefProps
> = (props, ref) => {
  const {
    formik,
    params,
    selected,
    onSelect,
    onSelectAll,
    orderId,
    formikHeader,
    openChangeOrderAdd,
    setOpenChangeOrderAdd,
    isChangeFlow,
    isViewMode,
    currentAction,
    changeCurrentAction,
    deleteChangeOrderLines,
    isNewItemAdded,
    setSubmitState,
    resetSubmitState,
    handleSubmitResponse,
    undoCancelOrderLine,
    setCurrentAction,
    tableLoading,
    setTableLoading,
    totalItemsOriginal,
    disabledActionForCashSale,
    cashSaleFormProps
  } = props;
  const classes = useStyles();
  const isOneTimeCustomer = formikHeader?.values?.isOneTimeCustomer;
  const isCashSaleOrder = checkIsCashSaleOrder(formikHeader?.values?.orderType);
  const initSplitObj = {
    index: -1, // orignalItem Index
    count: 0, // No.of Split
    removeSplit: false,
    removeIndex: -1 // for deleting split line
  };
  const [splitObj, setSplitObj] = useState(initSplitObj);

  const {
    currentData,
    pageInfo,
    handleNextPage,
    handlePreviousPage,
    handleNextLastPage,
    handlePreviousFirstPage,
    totalCount,
    disabled,
    settings,
    updateListSettings,
    toFirst
  } = usePagination(formik);

  function handleSplit(index, removeSplit) {
    setSplitObj(prev => ({
      index: removeSplit ? prev.index : index,
      count: prev.count + (removeSplit ? 0 : 1),
      removeSplit,
      removeIndex: removeSplit ? index : -1
    }));
  }
  const [isDeleteSuccess, setIsDeleteSuccess] = useState<boolean>(false);
  const handleDeleteLine = e => {
    deleteChangeOrderLines(e, setIsDeleteSuccess);
  };

  useEffect(() => {
    if (isDeleteSuccess && isChangeFlow) {
      handleSort(NotRefOrderSortField.itemNo);
      setIsDeleteSuccess(false);
    }
  }, [isDeleteSuccess]);
  const {
    tabList,
    columnsList,
    activeTab,
    onTabChange,
    searchModal,
    handleCloseSearchModal,
    emptyMiddleware,
    shipToModal,
    handleCloseShipToModal,
    handleOpenSearchModal,
    saleTextModal,
    handleCloseSaleTextModal
  } = useItemDetailsData({
    formik,
    isChangeFlow,
    isViewMode,
    handleSplit,
    deleteChangeOrderLines: handleDeleteLine,
    formikHeader,
    paymentTermList: formikHeader.values?.paymentTermList,
    undoCancelOrderLine,
    currentAction,
    disabledActionForCashSale
  });
  const { handleSort } = useSort(
    formik,
    params,
    toFirst,
    orderId,
    isChangeFlow
  );
  const sort = getSortParams(params);

  useImperativeHandle(ref, () => ({
    forceChangeTab: (tab: ItemTab) => onTabChange(tab)
  }));

  useEffect(() => {
    if (openChangeOrderAdd) {
      handleOpenSearchModal(totalCount + "0", null);
      setOpenChangeOrderAdd(false);
    }
  }, [openChangeOrderAdd]);

  const { handleAddToOrder, handleAddToChangeOrder } = useAddOrderLine({
    formikHeader,
    formikItemDetails: formik,
    handleCloseSearchModal,
    searchModal,
    sort,
    handleSort,
    setTableLoading,
    setSubmitState,
    totalItemsOriginal
  });
  const {
    deleteOtcShipTo,
    handleOTCShipTO,
    handleShipToSubmit
  } = useOrderLineShipTo({
    formikItemDetails: formik,
    shipToModal,
    formikHeader
  });

  const [openConfirmSplit, setOpenConfirmSplit] = useState<boolean>(false);

  const { saveSplitItem } = useSaveSplitItem({
    orderId,
    formikItemDetails: formik,
    setSubmitState,
    resetSubmitState,
    handleSubmitResponse,
    splitObj,
    params,
    totalItemsOriginal
  });

  const weightCalculator = row => {
    const calculation = row?.salesUnitList?.find(
      item => item.toUnit === row?.salesUnit
    )?.calculation;
    return calculation * (+row?.quantity || 0);
  };

  useEffect(() => {
    if (currentAction !== "split" && !isEqual(splitObj, initSplitObj)) {
      setSplitObj(initSplitObj);
    }
  }, [currentAction]);
  const UpdateBomWeightQty = (index, prev) => {
    if (prev[index].bomFlag && !prev[index]?.parentItemNo) {
      let totalParentWeight = 0.0;
      for (let i = index + 1; i < prev.length; i++) {
        if (
          prev[i]?.parentItemNo &&
          prev[i]?.parentItemNo === prev[index]?.itemNo
        ) {
          prev[i].quantity = prev[i].bomUnitQuantity * prev[index].quantity;
          prev[i].weight = weightCalculator(prev[i]);
          totalParentWeight += prev[i].weight;
        } else {
          break;
        }
      }
      return totalParentWeight;
    } else if (prev[index].bomFlag && prev[index]?.parentItemNo) {
      const prevWeight = prev[index].weight;
      const newWeight = weightCalculator(prev[index]);
      const parentItem = getItemByNo(prev, prev[index].parentItemNo);
      parentItem.weight += newWeight - prevWeight;
      return newWeight;
    } else {
      return null;
    }
  };

  useEffect(() => {
    if (formik.values?.currentIndex !== -1) {
      let prev = [...formik.values.lines];
      let index = formik.values.currentIndex;
      if (!params?.asc) {
        prev = prev?.reverse();
        index = prev?.length - index - 1;
      }
      if (prev[index] && Object.keys(prev[index])?.length) {
        const bomWeight = UpdateBomWeightQty(index, prev);
        if (bomWeight) {
          prev[index].weight = bomWeight;
        } else {
          prev[index].weight = weightCalculator(prev[index]);
        }
        prev[index].netValue = prev[index].quantity * prev[index].pricePerUnit;
        if (prev[index]?.isSplit && splitObj.index !== -1) {
          let count = 0;
          let totalSplitQty = 0;
          let splitIndex = splitObj.index;
          let i = splitIndex;
          if (!params.asc) {
            splitIndex = prev.length - splitIndex - 1;
            i = 0;
          }

          // calculate total split qty
          while (count < splitObj.count && i < prev.length) {
            if (prev[i]?.isSplit && !prev[i]?.parentItemNo) {
              totalSplitQty += +prev[i]?.quantity;
              count++;
            }
            i++;
          }
          const remain = prev[splitIndex].originalQuantity - totalSplitQty;
          prev[splitIndex].quantity = remain > 0 ? remain : 0;
          prev[splitIndex].netValue =
            prev[splitIndex].pricePerUnit * prev[splitIndex].quantity;
          // if material is BOM update child qty
          const splitBomWeight = UpdateBomWeightQty(splitIndex, prev);
          if (splitBomWeight) {
            prev[splitIndex].weight = splitBomWeight;
          } else {
            prev[splitIndex].weight = weightCalculator(prev[splitIndex]);
          }
        }

        if (!params?.asc) {
          prev = prev?.reverse();
        }
        formik.setFieldValue("lines", prev);
      }
    }
  }, [
    formik.values?.lines[formik.values?.currentIndex]?.quantity,
    formik.values?.lines[formik.values?.currentIndex]?.salesUnit
  ]);

  useEffect(() => {
    if (formik.values?.currentIndex !== -1) {
      const index = formik.values.currentIndex;
      if (
        formik.values?.lines[index] &&
        Object.keys(formik.values?.lines[index])?.length
      ) {
        const netValue =
          formik.values?.lines[index].pricePerUnit *
          formik.values?.lines[index].quantity;
        formik.setFieldValue(
          `lines[${formik.values?.currentIndex}].${"netValue"}`,
          netValue
        );
      }
    }
  }, [formik.values?.lines[formik.values?.currentIndex]?.pricePerUnit]);

  const disableRow = row => {
    if (isNewItemAdded && isChangeFlow && currentAction === "add") {
      return !row?.isNewItem;
    }
    if (isChangeFlow && currentAction === "split") {
      return !(row?.splitOriginal || row?.isSplit);
    }
    if (isChangeFlow && row?.itemStatusEn === ITEM_STATUS.CANCEL) {
      return row?.itemStatusEn === ITEM_STATUS.CANCEL;
    }
    if (
      isChangeFlow &&
      !isViewMode &&
      row.parentItemNo &&
      selected.includes(row.parentItemNo)
    ) {
      return true;
    }

    return false;
  };

  const rowsData = currentData();

  useEffect(() => {
    if (splitObj.count > 0) {
      if (currentAction !== "split") {
        changeCurrentAction("split");
      }
      let index = splitObj.index;
      let lines: any[] = [...formik.values.lines];
      if (splitObj.removeSplit) {
        let removeIndex = splitObj.removeIndex;
        if (!params?.asc) {
          lines = lines.reverse();
          removeIndex = lines.length - removeIndex - 1;
          index = lines?.length - index - 1;
        }
        let removeCount = 1;
        if (lines[removeIndex]?.bomFlag && !lines[removeIndex]?.parentItemNo) {
          for (let i = removeIndex + 1; i < lines.length; i++) {
            if (
              lines[i]?.parentItemNo &&
              lines[i]?.parentItemNo === lines[removeIndex]?.itemNo
            ) {
              removeCount++;
            } else {
              break;
            }
          }
        }
        // after removing last splitted item reset Orignal item
        if (lines[index]?.splitOriginal) {
          if (splitObj.count === 1) {
            lines[index].splitOriginal = false;
            lines[index].quantity = lines[index].originalQuantity;
            formik.setFieldValue("isSplitSubmit", false);
          } else {
            lines[index].quantity =
              +lines[index]?.quantity + +lines[removeIndex]?.quantity;
          }
          const resetWeight = UpdateBomWeightQty(index, lines);
          if (resetWeight) {
            lines[index].weight = resetWeight;
          } else {
            lines[index].weight = weightCalculator(lines[index]);
          }
        }
        if (
          lines[index]?.bomFlag &&
          !lines[index]?.parentItemNo &&
          splitObj.count === 1
        ) {
          for (let i = index + 1; i < lines.length; i++) {
            if (
              lines[i]?.parentItemNo &&
              lines[i]?.parentItemNo === lines[index]?.itemNo
            ) {
              if (lines[i]?.splitOriginal) {
                lines[i].splitOriginal = false;
                lines[i].quantity = lines[i].originalQuantity;
              }
            } else {
              break;
            }
          }
        }

        lines.splice(removeIndex, removeCount);
        if (!params.asc) {
          lines = lines.reverse();
          index = splitObj.index;
          removeIndex = splitObj.removeIndex;
        }
        // reset the split obj also
        setSplitObj(prev => ({
          ...prev,
          count: prev.count - 1,
          removeSplit: false
        }));
      } else if (splitObj.removeIndex === -1) {
        // create split lines
        const splitItems = [
          {
            ...lines[index],
            isSplit: true,
            splitOriginal: false,
            quantity: "",
            weight: 0,
            requestDate: null,
            productionFlag: "ไม่ผลิต",
            netValue: 0,
            saleText_1: lines[index]?.salesTextMaster?.saleText1 ?? "",
            saleText_2: lines[index]?.salesTextMaster?.saleText2 ?? "",
            saleText_3: lines[index]?.salesTextMaster?.saleText3 ?? "",
            saleText_4: lines[index]?.salesTextMaster?.saleText4 ?? ""
          }
        ];
        let splitOffset = 1;
        if (!lines[index]?.splitOriginal) {
          lines[index].splitOriginal = true;
        }
        // if item is BOM add child items to the split lines list
        if (!params.asc) {
          lines = lines.reverse();
          index = lines?.length - index - 1;
        }
        if (lines[index]?.bomFlag && !lines[index]?.parentItemNo) {
          for (let i = index + 1; i < lines.length; i++) {
            if (
              lines[i]?.parentItemNo &&
              lines[i]?.parentItemNo === lines[index]?.itemNo
            ) {
              splitItems.push({
                ...lines[i],
                isSplit: true,
                quantity: "",
                weight: 0,
                splitOriginal: false,
                productionFlag: "",
                netValue: 0,
                saleText_1: lines[i]?.salesTextMaster?.saleText1 ?? "",
                saleText_2: lines[i]?.salesTextMaster?.saleText2 ?? "",
                saleText_3: lines[i]?.salesTextMaster?.saleText3 ?? "",
                saleText_4: lines[i]?.salesTextMaster?.saleText4 ?? ""
              });
              splitOffset++;
              if (!lines[i]?.splitOriginal) {
                lines[i].splitOriginal = true;
              }
            } else {
              break;
            }
          }
        }

        let reverseOffset = 0;
        if (!params.asc) {
          lines = lines.reverse();
          index = splitObj.index;
          reverseOffset = splitOffset - 1;
          splitItems.reverse();
        }
        const startIndex = index - reverseOffset + splitOffset * splitObj.count;
        lines.splice(startIndex, 0, ...splitItems);
      }
      formik.setFieldValue("lines", lines);
    } else {
      if (currentAction === "split") {
        setCurrentAction("none");
      }
    }
  }, [splitObj.count, splitObj.removeSplit]);
  const deriveRowStyle = row => {
    if (row?.parentItemNo && !row?.isSplit && !row?.splitOriginal) {
      return classes.bomRow;
    } else if (row?.isSplit || row?.splitOriginal) {
      return classes.splitRow;
    } else {
      return "";
    }
  };

  const validateSplit = formik => {
    let isValid = true;
    const lines = [...formik.values.lines];
    const errors = formik.errors?.lines;
    for (let i = splitObj.index; i <= lines?.length; i++) {
      if (lines[i]?.isSplit || lines[i]?.splitOriginal) {
        if (lines[i]?.isSplit && !lines[i]?.parentItemNo) {
          if (errors?.[i]?.quantity || errors?.[i]?.requestDate) {
            isValid = false;
          }
        }
      } else {
        break;
      }
    }
    return isValid;
  };

  const handleSplitSubmit = () => {
    formik.setFieldValue("isSplitSubmit", true);
    // split data available in formik.values?.lines
    // original item flag => splitOriginal
    // splitted Items flag => isSplit
    // orignal quantity => originalQuantity
    if (validateSplit(formik)) {
      const prev = formik?.values?.lines;
      const index = params?.asc ? prev?.length - 1 : 0;
      let latestItemNo = prev?.[index].itemNo;
      const splitUpdatedItems = prev.map(line => {
        if (line.isSplit && !line.splitOriginal) {
          latestItemNo = (parseInt(latestItemNo, 10) + 10).toString();
          return {
            ...line,
            splitItemNo: latestItemNo
          };
        }
        return line;
      });
      formik.setFieldValue("lines", splitUpdatedItems);

      setOpenConfirmSplit(true);
      setSubmitState({ state: "splitConfirmPopup", context: null });
    }
  };
  const handleSplitSubmitConfirmOk = () => {
    saveSplitItem();
    setOpenConfirmSplit(false);
  };

  const selectTotalCount = useMemo(() => {
    if (isChangeFlow) {
      return (
        formik?.values?.lines?.filter(
          row => row?.itemStatusEn !== ITEM_STATUS.CANCEL
        ).length || 0
      );
    }
    return formik?.values?.lines?.filter(row => !row?.parentItemNo)?.length;
  }, [formik?.values?.lines]);
  const shipToData = useMemo(
    () =>
      getShipToData(formik?.values?.lines?.[shipToModal.index], formikHeader),
    [formik?.values?.lines?.[shipToModal.index], formikHeader]
  );

  function traverseAndUpdateBom(
    index,
    lines,
    parentItemNo,
    fieldName,
    parentValue,
    formik
  ) {
    if (sort.asc) {
      for (let i = index + 1; i < lines?.length; i++) {
        if (lines[i]?.parentItemNo === parentItemNo) {
          formik.setFieldValue(`lines[${i}].${fieldName}`, parentValue);
        } else {
          break;
        }
      }
    } else {
      for (let i = index - 1; i >= 0; i--) {
        if (lines[i]?.parentItemNo === parentItemNo) {
          formik.setFieldValue(`lines[${i}].${fieldName}`, parentValue);
        } else {
          break;
        }
      }
    }
  }

  function updateBomChildValues(formik, fieldName) {
    if (formik.values?.currentIndex !== -1) {
      const index = formik.values.currentIndex;
      const lines = [...formik.values.lines];
      if (lines?.[index]?.bomFlag && !lines?.[index]?.parentItemNo) {
        const parentItemNo = lines[index]?.itemNo;
        const parentValue = lines[index]?.[fieldName];
        traverseAndUpdateBom(
          index,
          lines,
          parentItemNo,
          fieldName,
          parentValue,
          formik
        );
      }
    }
  }

  const [previousProductionFlags, setPreviousProductionFlags] = useState([]);

  useEffect(() => {
    const currentProductionFlags = formik.values?.lines.map(
      item => item.productionFlag
    );

    const currentIndex = formik.values?.currentIndex;
    const hasChanged = currentProductionFlags.some(
      (flag, index) => flag !== previousProductionFlags[index]
    );

    if (hasChanged) {
      setPreviousProductionFlags(currentProductionFlags);
    }

    if (
      currentIndex >= 0 &&
      previousProductionFlags?.[currentIndex] !==
        formik.values?.lines[currentIndex]?.productionFlag
    ) {
      updateBomChildValues(formik, "productionFlag");
    }
  }, [formik.values?.lines]);

  useEffect(() => {
    if (isChangeFlow && currentAction === "add") {
      updateBomChildValues(formik, "plant");
    }
  }, [formik.values?.lines[formik.values?.currentIndex]?.plant]);

  useEffect(() => {
    updateBomChildValues(formik, "requestDate");
  }, [formik.values?.lines[formik.values?.currentIndex]?.requestDate]);

  useEffect(() => {
    updateBomChildValues(formik, "priceDate");
  }, [formik.values?.lines[formik.values?.currentIndex]?.priceDate]);

  const getDisableRequestDate = () => {
    const isDisableStatus =
      formikHeader?.values?.orderStatus === ITEM_STATUS.COMPLETE_DELIVERY ||
      formikHeader?.values?.orderStatus === ITEM_STATUS.CANCEL;
    return isChangeFlow && isDisableStatus;
  };

  const renderSubTabs = (
    <div className={classes.tabBox}>
      {renderCollection(tabList, tab => {
        if (tab.code === ItemTab.tabD && !isCashSaleOrder) {
          return null;
        }

        return (
          <div
            key={tab.code}
            className={classNames(
              activeTab === tab.code ? classes.zMax : classes[tab.index]
            )}
          >
            <Button
              id={tab.id}
              data-testid={tab.id}
              onClick={() => onTabChange(tab.code)}
              className={classNames(
                !tab.first && classes.tabB,
                activeTab === tab.code
                  ? classes.tableTabActive
                  : classes.tableTab
              )}
            >
              <MyTypography
                variant={"body2"}
                className={classNames(
                  activeTab === tab.code
                    ? classes.innerTabActive
                    : classes.innerTab
                )}
              >
                {tab.title}
              </MyTypography>
            </Button>
          </div>
        );
      })}
    </div>
  );

  return (
    <div data-testid="addEditItemDetailsNotRef" className={classes.root}>
      {tabList?.length > 0 && renderSubTabs}

      <AdvancedTable
        cellAlign="center"
        headHeight={48}
        cellheight={activeTab === ItemTab.tabC ? 72 : 48}
        columnsList={columnsList}
        rowsData={rowsData}
        handleSort={() => {
          handleSort(NotRefOrderSortField.itemNo);
        }}
        sortDirection={new Map([["itemNo", getArrowDirection(sort.asc)]])}
        tableKey="itemNo"
        rootClassName={classNames(
          classes.advancedTableRoot,
          ![ItemTab.tabA, ItemTab.tabC].includes(activeTab) && classes.hidden
        )}
        selectProps={{
          selected,
          onSelect,
          onSelectAll,
          className: classes.freezCell,
          style: { left: 0 }
        }}
        totalCount={selectTotalCount}
        middleware={emptyMiddleware}
        borderRequired={true}
        dynamicRowStyle={deriveRowStyle}
        hideSelect={row => (row?.parentItemNo && !isChangeFlow) || row?.isSplit}
        reRenderKey="id"
        isViewMode={isViewMode}
        getIsLineViewMode={row => row?.isSplit || row?.splitOriginal}
        getIsDisabled={row => disableRow(row)}
        disableAllCheckBox={currentAction === "split"}
        hideAllSelect={(isChangeFlow && currentAction === "add") || isViewMode}
        onSubmitSplit={handleSplitSubmit}
        errors={formik.errors?.lines}
        isSplitSubmitted={formik.values?.isSplitSubmit}
        selectAllLabel={isChangeFlow ? "Cancel" : "Delete"}
        currentAction={currentAction}
        disableSort={currentAction === "split"}
        isLinesEmpty={!rowsData[0]?.materialCode}
        disableRequestDate={getDisableRequestDate()}
        loading={tableLoading}
      />

      {activeTab === ItemTab.tabD && cashSaleFormProps && (
        <CashSaleForm {...cashSaleFormProps} />
      )}

      <ModalSearchForProducts
        loading={tableLoading}
        open={searchModal?.isOpen}
        onClose={() => handleCloseSearchModal()}
        handleAddToOrder={e =>
          isChangeFlow ? handleAddToChangeOrder(e) : handleAddToOrder(e)
        }
        headerData={formikHeader}
      />
      <DialogConfirmSplit
        open={openConfirmSplit}
        onClose={() => setOpenConfirmSplit(false)}
        onOk={handleSplitSubmitConfirmOk}
      />
      {!!totalCount && [ItemTab.tabA, ItemTab.tabC].includes(activeTab) && (
        <TablePaginationCustom
          hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
          hasPreviousPage={
            pageInfo && !disabled ? pageInfo.hasPreviousPage : false
          }
          onNextPage={handleNextPage}
          onPreviousPage={handlePreviousPage}
          totalCount={totalCount}
          selectColor="#007E92"
          settings={settings}
          onUpdateListSettings={updateListSettings}
          isBackFistPage
          isNextLastPage
          latestPageItemNumber={totalCount}
          onNextLastPage={handleNextLastPage}
          onPreviousFirstPage={handlePreviousFirstPage}
          disabled={disabled}
        />
      )}
      {shipToModal.isOpen &&
        (!isOneTimeCustomer ? (
          <ModalAddress
            address={formikHeader?.values?.shipTolist}
            initialValue={(
              formik?.values?.lines?.[shipToModal.index]?.shipTo ||
              formikHeader.values?.shipTo
            )
              ?.split("-")[0]
              ?.trim()}
            visible={shipToModal.isOpen}
            onClose={handleCloseShipToModal}
            addressType="shipTo"
            formik={formikHeader}
            customSubmit={value =>
              handleShipToSubmit(shipToModal.index, "shipTo", value)
            }
          />
        ) : (
          <ModalOneTimeForms
            visible={shipToModal.isOpen}
            formType="shipTo"
            formikHeader={formikHeader}
            onClose={handleCloseShipToModal}
            otcInitialValue={shipToData?.otcV}
            searchInitialValue={shipToData?.v}
            onSearchSubmit={(name, Deletename, value) => {
              handleShipToSubmit(shipToModal.index, name, value);
              deleteOtcShipTo(shipToModal.index, Deletename, null);
            }}
            onSubmit={(name, Deletename, otc) => {
              handleOTCShipTO(shipToModal.index, name, Deletename, { ...otc });
            }}
            isChangeFlow={isChangeFlow}
            currentAction={currentAction}
          />
        ))}
      {saleTextModal?.isOpen && (
        <SaleTextModal
          visible={saleTextModal?.isOpen}
          initialValue={saleTextModal.saleTextValue}
          formik={formik}
          title={saleTextModal.title}
          saleTextKey={saleTextModal.key}
          saleTextIndex={saleTextModal.index}
          maxLength={40}
          onClose={() => {
            handleCloseSaleTextModal();
          }}
          onSubmit={() => {
            handleCloseSaleTextModal();
          }}
        />
      )}
    </div>
  );
};

export default forwardRef(AddEditItemDetailsNotRef);
